import React, { useState } from "react";
import {
  Box, Button, Container, Grid, Heading, HStack, Text, VStack, Badge,
  UnorderedList, ListItem, FormControl, FormLabel, Input, Textarea, Divider, Stat,
  StatLabel, StatNumber, StatHelpText, Link,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

const brand = {
  green: "#1C4C3F",
  cream: "#F7F5EB",
  mint: "#D9EFE6",
  ink: "#0E2721",
};

const CONTACT_EMAIL = "hello@shelfkey.co";

/* Small reusable pill */
const Pill = ({ children }) => (
  <Box
    as="span"
    px={3}
    py={1}
    borderRadius="full"
    bg="whiteAlpha.900"
    border="1px solid rgba(28,76,63,0.15)"
    fontSize="sm"
    color="#1C4C3F"
    fontWeight="medium"
  >
    {children}
  </Box>
);

/* Simple feature card */
function FeatureCard({ title, body }) {
  return (
    <Box p={6} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg">
      <VStack align="start" spacing={3}>
        <Badge bg={brand.mint} color={brand.ink} borderRadius="md" px={2} py={1}>
          {title}
        </Badge>
        <Text>{body}</Text>
      </VStack>
    </Box>
  );
}

/* Reusable form used in both the modal and the bottom section */
function WaitlistForm({ email, setEmail, company, setCompany, useCase, setUseCase }) {
  const openMailtoDraft = () => {
    const subject = encodeURIComponent("ShelfKey Insights – Early Access Request");
    const body = encodeURIComponent(
      [
        "Hi ShelfKey Team,",
        "",
        "I'd like early access to ShelfKey Insights.",
        `Email: ${email || ""}`,
        `Company: ${company || ""}`,
        "",
        "Use case / goals:",
        useCase || "",
        "",
        "---",
        "Sent from the Insights page",
      ].join("\n")
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <VStack align="stretch" spacing={4}>
      <FormControl isRequired>
        <FormLabel>Work Email</FormLabel>
        <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel>Company (optional)</FormLabel>
        <Input value={company} onChange={(e)=>setCompany(e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel>What do you want from Insights?</FormLabel>
        <Textarea
          placeholder="e.g., Understand promo ROI at Retailer X; build buyer story for Rockies; price move from $5.99 → $6.49"
          value={useCase}
          onChange={(e)=>setUseCase(e.target.value)}
        />
      </FormControl>
      <Button colorScheme="green" onClick={openMailtoDraft} isDisabled={!email}>
        Email us your request
      </Button>
      <Text fontSize="sm" opacity={0.7}>
        We’ll only email you about ShelfKey Insights. Opt out anytime.
      </Text>
    </VStack>
  );
}

export default function InsightsPage() {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // shared state for both modal + section form
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [useCase, setUseCase] = useState("");

  return (
    <Box bg={brand.cream} color={brand.ink}>
      {/* TOP BAR: Back to Home only */}
      <Container
        maxW="6xl"
        px={{ base: 4, md: 6 }}
        py={4}
        position="sticky"
        top="0"
        zIndex="10"
        bg={brand.cream}
        boxShadow="sm"
      >
        <HStack justify="space-between">
          <HStack spacing={3} align="center">
            <Button
              as={RouterLink}
              to="/"
              variant="ghost"
              color={brand.green}
              leftIcon={<span style={{ fontSize: 18 }}>←</span>}
            >
              Back to Home
            </Button>
          </HStack>
          <HStack>
            <Box as="span" display="inline-flex" alignItems="center">
              <img src="/favicon-64x64.png" alt="ShelfKey logo" width="24" height="24" />
            </Box>
            <Heading size="sm" color={brand.green}>ShelfKey</Heading>
          </HStack>
        </HStack>
      </Container>

      {/* HERO */}
      <Box bg={brand.green} color="white">
        <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 14, md: 20 }}>
          <VStack align="start" spacing={6}>
            <HStack spacing={3}>
              <Pill>Coming soon</Pill>
              <Pill>AI-native</Pill>
              <Pill>For CPG teams</Pill>
            </HStack>
            <Heading as="h1" size="2xl" lineHeight={1.1}>
              ShelfKey Insights
            </Heading>


            <Text 
              as="h2"
              fontSize="lg"
              fontWeight="semibold"
              opacity={0.95}
              maxW="3x1"
            >
              Powered by Quantum™ — ShelfKey’s AI decision intelligence engine.
            </Text>

            <Text fontSize="xl" opacity={0.95} maxW="3x1">
              Advanced decision-grade analytics for CPG—without a $300K+/yr platform or agency lock-in.
              Upload your files, get drivers, promo ROI, pricing scenarios, and buyer-ready takeaways in minutes.
            </Text>
            <HStack spacing={4}>
              <Button
                size="lg"
                bg="white"
                color={brand.green}
                _hover={{ bg: brand.mint }}
                onClick={onOpen}
              >
                Join the waitlist
              </Button>
              <Text opacity={0.85}>Priority access for early brands • Limited seats</Text>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* VALUE PROPS */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
          <FeatureCard
            title="Business Drivers"
            body="See what moved performance: distribution, velocity, price, promo—ranked by contribution and confidence."
          />
          <FeatureCard
            title="Promo Intelligence"
            body="Expected lift, ROAS, and the next-best promo by retailer. Stop guessing; set guardrails."
          />
          <FeatureCard
            title="Pricing Scenarios"
            body="Elasticity-aware ‘what-ifs’: revenue and margin deltas at target price points or TPR stacks."
          />
        </Grid>

        <Grid mt={6} templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
          <FeatureCard
            title="Buyer-Ready Output"
            body="Slide-ready bullets for each chart: What happened • Why it matters • What to do next."
          />
          <FeatureCard
            title="File-First Workflow"
            body="Drop CSV/XLSX → get clean charts + insights. Export PNG/PDF for decks."
          />
          <FeatureCard
            title="Privacy by Default"
            body="No model training on your private data. One-click purge. AES-256 at rest, TLS in transit."
          />
        </Grid>
      </Container>

      {/* HOW IT WORKS */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 8, md: 10 }}>
        <Box bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={{ base: 5, md: 6 }}>
          <Heading size="md" mb={2}>How it works</Heading>
          <Text fontSize="sm" opacity={0.8} mb={4}>
            Built for speed—no custom implementations, no six-month “phase 1.”
          </Text>
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={6}>
            <Box>
              <Badge bg={brand.mint} color={brand.ink} mb={2}>1</Badge>
              <Text fontWeight="semibold">Upload</Text>
              <Text fontSize="sm" opacity={0.85}>CSV/XLSX by brand, retailer, or time window.</Text>
            </Box>
            <Box>
              <Badge bg={brand.mint} color={brand.ink} mb={2}>2</Badge>
              <Text fontWeight="semibold">Configure</Text>
              <Text fontSize="sm" opacity={0.85}>Pick metrics: ACV, velocity, price, promo flags.</Text>
            </Box>
            <Box>
              <Badge bg={brand.mint} color={brand.ink} mb={2}>3</Badge>
              <Text fontWeight="semibold">Generate</Text>
              <Text fontSize="sm" opacity={0.85}>Charts + drivers + recommended actions.</Text>
            </Box>
            <Box>
              <Badge bg={brand.mint} color={brand.ink} mb={2}>4</Badge>
              <Text fontWeight="semibold">Export</Text>
              <Text fontSize="sm" opacity={0.85}>PNG/PDF plus a one-page summary for your buyer deck.</Text>
            </Box>
          </Grid>
        </Box>
      </Container>

      {/* LIGHT COST COMPARISON */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 8, md: 12 }}>
        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
          <Stat p={6} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg">
            <StatLabel>Traditional platform + services</StatLabel>
            <StatNumber>$300K+ / yr</StatNumber>
            <StatHelpText>Multi-month onboarding • heavy lift</StatHelpText>
          </Stat>
          <Stat p={6} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg">
            <StatLabel>Agency decks</StatLabel>
            <StatNumber>$50K–$150K</StatNumber>
            <StatHelpText>Per project • limited refresh</StatHelpText>
          </Stat>
          <Stat p={6} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg">
            <StatLabel>ShelfKey Insights</StatLabel>
            <StatNumber>Fraction of the cost</StatNumber>
            <StatHelpText>AI-native • file-first • fast</StatHelpText>
          </Stat>
        </Grid>
      </Container>

      {/* MODULE LIST */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 4, md: 8 }}>
        <Box bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={{ base: 5, md: 6 }}>
          <Heading size="md" mb={3}>Planned modules</Heading>
          <UnorderedList spacing={2}>
            <ListItem><strong>Business Drivers:</strong> mix, distribution, velocity, price, promo.</ListItem>
            <ListItem><strong>Promo Planner:</strong> expected lift & ROAS by retailer/week.</ListItem>
            <ListItem><strong>Price Scenarios:</strong> simulate new SRPs/TPRs with guardrails.</ListItem>
            <ListItem><strong>Retailer Stories:</strong> buyer-ready bullets by banner/region.</ListItem>
            <ListItem><strong>Anomaly Watch:</strong> out-of-stocks, delist risk, margin hits.</ListItem>
          </UnorderedList>
        </Box>
      </Container>

      {/* FAQ */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 8, md: 10 }}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
          <Box bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={5}>
            <Heading size="sm" mb={2}>What files do you support?</Heading>
            <Text fontSize="sm" opacity={0.9}>
              CSV and Excel to start. We’ll add connectors later based on demand.
            </Text>
          </Box>
          <Box bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={5}>
            <Heading size="sm" mb={2}>Do you train on my data?</Heading>
            <Text fontSize="sm" opacity={0.9}>
              No. Your data is processed to generate outputs, then you control what’s saved or deleted.
            </Text>
          </Box>
          <Box bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={5}>
            <Heading size="sm" mb={2}>Will it replace my analyst?</Heading>
            <Text fontSize="sm" opacity={0.9}>
              It automates repetitive steps so your team focuses on decisions—not formatting or formulas.
            </Text>
          </Box>
          <Box bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={5}>
            <Heading size="sm" mb={2}>What’s the pricing?</Heading>
            <Text fontSize="sm" opacity={0.9}>
              Final tiers TBD. Early access brands get preferred pricing.
            </Text>
          </Box>
        </Grid>
      </Container>

      {/* WAITLIST (Section, mirrors modal) */}
      <Container id="waitlist" maxW="3xl" px={{ base: 4, md: 6 }} pb={{ base: 12, md: 16 }}>
        <Divider mb={8} />
        <VStack align="stretch" spacing={4} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={{ base: 5, md: 6 }}>
          <Heading size="md">Join the Insights waitlist</Heading>
          <Text opacity={0.85}>
            Tell us where you’re stuck (promo ROI, ACV growth, buyer narrative, etc.). We’ll prioritize invites where we can help fastest.
          </Text>
          <WaitlistForm
            email={email} setEmail={setEmail}
            company={company} setCompany={setCompany}
            useCase={useCase} setUseCase={setUseCase}
          />
          <Text fontSize="sm" opacity={0.7}>
            AI-generated outputs are for informational purposes only and should not replace professional judgment.{" "}
            <Link as={RouterLink} to="/terms" textDecoration="underline" color={brand.green}>Terms</Link> •{" "}
            <Link as={RouterLink} to="/privacy" textDecoration="underline" color={brand.green}>Privacy</Link>
          </Text>
        </VStack>
      </Container>

      {/* MODAL: opens from hero CTA */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Request early access</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <WaitlistForm
              email={email} setEmail={setEmail}
              company={company} setCompany={setCompany}
              useCase={useCase} setUseCase={setUseCase}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
